import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type {
  SupportAnalyticsResponse,
  SupportCategoryAdminResponse,
  SupportCannedResponseResponse,
  SupportTagResponse,
  SupportUserRef,
} from '@/lib/api';

interface AdminTicketSummary {
  ticketId: string;
  ticketNumber: number;
  subject: string;
  status: string;
  priority: string | null;
  aiPriority: string | null;
  categoryId: string | null;
  userId?: string;
  user?: SupportUserRef | null;
  archeId?: string | null;
  description?: string;
  assignedTo?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: SupportTagResponse[];
}

interface UseAdminTicketsOptions {
  status?: string | string[];
  priority?: string;
  categoryId?: string;
  assignedTo?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  tagId?: string;
  archeId?: string;
  includeDeleted?: boolean;
}

interface UseAdminTicketsReturn {
  tickets: AdminTicketSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
  analytics: SupportAnalyticsResponse | null;
  categories: SupportCategoryAdminResponse[];
  cannedResponses: SupportCannedResponseResponse[];
  tags: SupportTagResponse[];
  refreshAll: () => void;
}

const LIMIT = 20;

export function useAdminTickets(options: UseAdminTicketsOptions = {}): UseAdminTicketsReturn {
  const ticketsQuery = useInfiniteQuery({
    queryKey: queryKeys.customerSupport.adminTickets({
      status: options.status,
      priority: options.priority,
      categoryId: options.categoryId,
      assignedTo: options.assignedTo,
      search: options.search,
      sortBy: options.sortBy,
      sortOrder: options.sortOrder,
      tagId: options.tagId,
      archeId: options.archeId,
      includeDeleted: options.includeDeleted,
    }),
    queryFn: async ({ pageParam = 0 }) => {
      return api.listAllSupportTickets({
        status: options.status || undefined,
        priority: options.priority || undefined,
        categoryId: options.categoryId || undefined,
        assignedTo: options.assignedTo || undefined,
        search: options.search || undefined,
        sortBy: options.sortBy || undefined,
        sortOrder: options.sortOrder || undefined,
        tagId: options.tagId || undefined,
        archeId: options.archeId || undefined,
        includeDeleted: options.includeDeleted || undefined,
        limit: LIMIT,
        offset: pageParam,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.tickets.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const analyticsQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminAnalytics(),
    queryFn: () => api.getSupportAnalytics(),
  });

  const categoriesQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminCategories(),
    queryFn: () => api.listSupportCategoriesAdmin(),
  });

  const cannedResponsesQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminCannedResponses(),
    queryFn: () => api.listSupportCannedResponses(),
  });

  const tagsQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminTags(),
    queryFn: () => api.listSupportTags(),
  });

  const tickets = ticketsQuery.data?.pages.flatMap((p) => p.tickets) ?? [];
  const total = ticketsQuery.data?.pages[0]?.total ?? 0;

  return {
    tickets,
    total,
    loading: ticketsQuery.isLoading,
    error: ticketsQuery.error ? ticketsQuery.error.message : null,
    refresh: () => ticketsQuery.refetch(),
    loadMore: () => ticketsQuery.fetchNextPage(),
    hasMore: !!ticketsQuery.hasNextPage,
    analytics: analyticsQuery.data ?? null,
    categories: categoriesQuery.data?.categories ?? [],
    cannedResponses: cannedResponsesQuery.data?.responses ?? [],
    tags: tagsQuery.data?.tags ?? [],
    refreshAll: () => {
      ticketsQuery.refetch();
      analyticsQuery.refetch();
      categoriesQuery.refetch();
      cannedResponsesQuery.refetch();
      tagsQuery.refetch();
    },
  };
}
